import { useEffect, useMemo } from 'react';
import { useFlatData } from '@interxfi/api-client';
import { useIxcSweepsV1SweepPlanQueryServiceSweepPlanList } from '@interxfi/api-client/generated/hooks/ixc-sweeps-v1-sweep-plan-query-service-sweep-plan-list';
import { useProgramAccess } from '@interxfi/api-client/program/program-auth-access-selector';
import { getReadonlyListItemString } from '@interxfi/app-components/components/readonly-list/util';
import SweepsIcon from '@interxfi/design-system/icons/react/icon-sweeps';
import type { IxcPortfolioV1SweepsControls, IxcSweepsV1SweepPlanState } from '@interxfi/types/generated/api';
import { AccordionSummaryGroup } from '../../../../components/card-group/accordion-summary-group';
import type { CardSummaryItem } from '../../../../components/card-group/card-summary-group';
import { buildSweepPlanComboboxQuery } from '../../../../components/data-combobox-input/entity-config/sweep-plan';
import { programControlSections } from './program-control-sections';

function getSweepsItems(controls: IxcPortfolioV1SweepsControls | undefined, plans: IxcSweepsV1SweepPlanState[]): CardSummaryItem[] {
  const defaultSweepPlan = plans.find((plan) => plan.sweepPlanId === controls?.settings?.defaultSweepPlanId);

  return [
    {
      kind: 'permission',
      id: 'canSweep',
      item: {
        name: 'Allow Account Sweeps',
        value: controls?.settings?.canSweepAccounts,
        items: [
          getReadonlyListItemString(
            'eligiblePlans',
            'Eligible Sweep Plans',
            controls?.settings?.eligibleSweepPlanIds?.map((id) => plans.find((plan) => plan.sweepPlanId === id)?.data?.name || id).join(', '),
          ),
          getReadonlyListItemString('defaultSweepPlan', 'Default Sweep Plan', defaultSweepPlan?.data?.name || controls?.settings?.defaultSweepPlanId),
        ],
      },
    },
  ];
}

interface ProgramControlSweepsSummaryProps {
  controls: IxcPortfolioV1SweepsControls | undefined;
  isPending: boolean;
  programId: string | undefined;
}

export function ProgramControlSweepsSummary({ controls, isPending, programId }: ProgramControlSweepsSummaryProps) {
  const programOrClearingAccess = useProgramAccess(programId, true);
  const planQuery = useMemo(() => {
    if (!controls?.settings?.canSweepAccounts) {
      return undefined;
    }

    return buildSweepPlanComboboxQuery({
      sweepPlanId: Array.from(
        new Set([controls.settings.defaultSweepPlanId ?? '', ...(controls.settings.eligibleSweepPlanIds || [])].filter(Boolean)),
      ),
    });
  }, [controls?.settings]);
  const {
    data,
    isPending: isPlanQueryPending,
    hasNextPage,
    fetchNextPage,
  } = useIxcSweepsV1SweepPlanQueryServiceSweepPlanList(programOrClearingAccess ? planQuery : undefined, undefined, programOrClearingAccess);
  const plans = useFlatData(data, 'sweepPlan');
  const items = useMemo(() => getSweepsItems(controls, plans), [controls, plans]);

  // Automatically fetch the next page of sweep plans if there are more to ensure we have all plans needed for the summary items. This may be optimized in the future
  // if a bunch of these are selected. A collapsible table view (or similar) may be needed to support it.
  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  return (
    <AccordionSummaryGroup
      icon={<SweepsIcon aria-hidden />}
      id={programControlSections.sweeps}
      isPending={Boolean(isPending || (programOrClearingAccess && planQuery && isPlanQueryPending))}
      items={items}
      title="Sweeps"
    />
  );
}
